// Email link login card with invitation

import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Link, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Card from './Card';
import Input from './Input';
import isEmail from 'validator/lib/isEmail';
import {
  authCheckAuthByEmail,
  authSendSignInLinkToEmail,
} from '../utils/firebase';
import { useFirebase } from '../components/FirebaseContextProvider';
import { storageEmailForSignIn } from '../utils/consts';
import './AuthCard.scss';

export default function AuthCard() {
  const [sentTo, setSentTo] = useState(null);
  const [spinner, setSpinner] = useState(false);
  const { isUserResolved } = useFirebase();
  const location = useLocation();

  useEffect(() => {
    setSpinner(!isUserResolved);
  }, [isUserResolved]);

  const initialValues = {
    email: location.state?.email ?? '',
  };

  const validationSchema = new Yup.ObjectSchema({
    email: Yup.string()
      .test('is-valid-email', 'Invalid email', (value) => isEmail(value))
      .required('Required'),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const email = values.email;

      try {
        setSpinner(true);

        const checkEmailRes = await authCheckAuthByEmail({ email });
        if (!checkEmailRes?.data)
          throw new Error(
            'The email is not registered. Use an invitation link to register',
          );

        await authSendSignInLinkToEmail({
          email,
          link: `${process.env.REACT_APP_ROOT_URL}/emaillinksignin`,
        });
        localStorage.setItem(storageEmailForSignIn, email);
        setSentTo(email);
        toast.success(
          `Auth link was sent to ${email}, please check the inbox and the Spam folder`,
        );
      } catch (error) {
        toast.error(error?.message ?? 'Failed to send link');
      } finally {
        setSpinner(false);
      }
    },
  });

  return (
    <Card
      header={sentTo ? 'Auth link sent' : 'Get auth link'}
      className='auth-card'
      spinner={spinner}
    >
      {!sentTo && (
        <form className='auth-form' onSubmit={formik.handleSubmit}>
          <p className='font-16'>
            Please enter your email to get an auth link, or{' '}
            <Link
              to='/login'
              state={{ email: formik.values.email }}
              className='font-16'
            >
              log in with email & password
            </Link>
          </p>
          <Input
            name='email'
            placeholder='E-mail'
            {...formik.getFieldProps('email')}
            error={
              formik.touched.email && formik.errors.email
                ? formik.errors.email
                : null
            }
          />
          <button type='submit'>Send link</button>
        </form>
      )}
      {sentTo && (
        <>
          <p className='font-16'>
            The auth link is sent to{' '}
            <span className='font-medium'>{sentTo}</span>
          </p>
          <p className='font-16'>Please check the inbox and the Spam folder</p>
        </>
      )}
    </Card>
  );
}
